import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, Bell, LogOut, Mail, Sun, Moon } from 'lucide-react'
import { useAuth } from '../../core/auth'
import { useUnreadCounts } from '../contexts/UnreadCountsContext'
import { useActiveModule, MODULE_DEFINITIONS } from '../contexts/ModuleContext'

const routeNames: Record<string, string> = {
  '/lng-ops': 'Dashboard',
  '/lng-ops/live-view': 'Live View',
  '/lng-ops/itinerary': 'Itinerary',
  '/lng-ops/vessels': 'Vessels',
  '/lng-ops/voyage-performance': 'Voyage Performance',
  '/lng-ops/bog-management': 'BOG Management',
  '/lng-ops/vessel-reports': 'Vessel Reports',
  '/lng-ops/graphs': 'Graphs',
  '/lng-ops/reports': 'Reports',
  '/lng-ops/scheduler': 'Scheduler',
  '/lng-ops/sscs': 'SSCS',
  '/lng-ops/calculators': 'Calculators',
  '/lng-ops/messages': 'Messages',
  '/lng-ops/charterers': 'Charterers',
  '/lng-ops/admin': 'Admin',
  '/lng-ops/tasks': 'Tasks',
  '/fsru': 'Dashboard',
}

interface TopBarProps {
  onOpenCommandPalette: () => void
}

function useTheme() {
  const [theme, setThemeState] = useState<'dark' | 'light'>(() => {
    try {
      return (localStorage.getItem('theme') as 'dark' | 'light') || 'dark'
    } catch { return 'dark' }
  })

  const setTheme = (t: 'dark' | 'light') => {
    setThemeState(t)
    try { localStorage.setItem('theme', t) } catch { /* */ }
    if (t === 'light') {
      document.documentElement.classList.add('light')
    } else {
      document.documentElement.classList.remove('light')
    }
  }

  // Apply on mount
  useState(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light')
    }
  })

  return { theme, toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark') }
}

export default function TopBar({ onOpenCommandPalette }: TopBarProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [showMenu, setShowMenu] = useState(false)
  const activeModule = useActiveModule()
  const moduleDefinition = MODULE_DEFINITIONS.find((m) => m.id === activeModule)
  const currentPageName = routeNames[location.pathname] || ''
  const { messages: msgUnread, notifications: notifUnread } = useUnreadCounts()
  const { theme, toggleTheme } = useTheme()

  return (
    <header className="flex h-12 items-center justify-between bg-bg-secondary px-4 pl-12 lg:pl-4">
      {/* Left: Module name + page */}
      <div className="flex items-center gap-3">
        <h1 className="text-sm font-semibold tracking-wide text-text-primary">
          {moduleDefinition?.label ?? 'BW LNG'}
        </h1>
        <span className="rounded bg-accent-muted px-1.5 py-0.5 text-2xs font-medium text-accent">
          LIVE
        </span>
        {currentPageName && (
          <span className="text-xs text-text-muted">
            / {currentPageName}
          </span>
        )}
      </div>

      {/* Center: Search trigger for Command Palette */}
      <div className="flex max-w-md flex-1 items-center mx-8">
        <button
          onClick={onOpenCommandPalette}
          className="flex w-full items-center gap-2 rounded-lg bg-bg-tertiary py-1.5 pl-3 pr-2 text-xs text-text-muted transition-colors hover:border-border-light hover:bg-bg-tertiary"
        >
          <Search className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1 text-left">Search...</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded-light bg-bg-primary px-1.5 py-0.5 text-2xs font-mono">
            Ctrl+K
          </kbd>
        </button>
      </div>

      {/* Right: User */}
      <div className="flex items-center gap-2">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-bg-hover hover:text-text-primary transition-colors"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Mail icon — navigates to messages */}
        <button
          onClick={() => navigate('/lng-ops/messages')}
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-bg-hover hover:text-text-primary transition-colors"
          title="Messages"
        >
          <Mail className="h-4 w-4" />
          {msgUnread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 rounded-full bg-accent flex items-center justify-center text-[9px] font-bold text-accent-fg px-1">
              {msgUnread > 9 ? '9+' : msgUnread}
            </span>
          )}
        </button>

        <button
          onClick={() => navigate('/lng-ops/scheduler')}
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-bg-hover hover:text-text-secondary"
          title="Notifications"
        >
          <Bell className="h-4 w-4" />
          {notifUnread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 rounded-full bg-accent flex items-center justify-center text-[9px] font-bold text-accent-fg px-1">
              {notifUnread > 9 ? '9+' : notifUnread}
            </span>
          )}
        </button>

        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-text-muted transition-colors hover:bg-bg-hover hover:text-text-secondary"
          >
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-muted text-2xs font-semibold text-accent">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="text-xs text-text-secondary">{user?.name || 'User'}</span>
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full mt-1 z-50 w-48 rounded-lg bg-bg-tertiary shadow-lg">
                <div className="border-b border-border/10 px-3 py-2">
                  <p className="text-xs font-medium text-text-primary">{user?.name}</p>
                  <p className="text-2xs text-text-muted">{user?.email}</p>
                  <p className="text-2xs text-text-muted capitalize">{user?.role}</p>
                </div>
                <button
                  onClick={() => { setShowMenu(false); logout() }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-bg-hover hover:text-negative transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
